import { ComponentType } from '@angular/cdk/overlay';
import { computed, EventEmitter, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DashboardLayoutDimensions } from '../layout/dashboard-layout-dimensions';
import { DashboardLayoutService } from '../layout/dashboard-layout.service';
import { DialogAction, DialogActivity, DialogData } from './';
import { AngularFormDialog, AngularFormDialogData, ConfirmationDialog, MessageDialog } from './components';

const PADDINGS = {
	xs: 10,
	sm: 12,
	md: 20,
	lg: 25,
};

@Injectable({ providedIn: 'root' })
export class DialogService {

	public activity = new EventEmitter<DialogActivity>();

	constructor(
		private matDialog: MatDialog,
		private layout: DashboardLayoutService,
	) {
	}

	/**
	 *  Create and show a custom dialog.
	 * @param component The component to show in the dialog. It should @Inject(MAT_DIALOG_DATA) private dialog: DialogContext<D>
	 * @param reqW The requested width of the dialog. It may be different depending on the screen size
	 * @param reqH The requested height of the dialog. It may be different depending on the screen size
	 */
	async showCustom<C, D>(component: ComponentType<C>, data: DialogData<D>, reqW: number, reqH: number): Promise<DialogAction<any>> {

		return this.dialogActivityResolver<C, D>(component, data, reqW, reqH);
	}



	/**
	 *  Create and show a message dialog with one button to close it.
	 * 
	 * @param header The text shown in the dialog header
	 * @param message The displayed text, which can be a literal string or a LabelKey if it needs to be translated.
	 * @param buttonText The text on the button text, which can be a literal string or a LabelKey if it needs to be translated.
	 * @param bullets An array of strings to display as bullet items underneath the message. The requested height will be adjusted with an extra 21px for each bullet to try and make the variable number bullets visible.
	 * @param reqW the requested width of the dialog. It may be different depending on the screen size. Default: 400px
	 * @param reqH the requested height of the dialog. It may be different depending on the screen size. Default: 300px
	 */
	async showMessage(
		header: string,
		message: string,
		buttonText = 'Okay',
		bullets: string[] = [],
		reqW = 400,
		reqH = 300,
	): Promise<DialogAction> {

		if (bullets.length) reqH += (bullets.length) * 21 + 35;	// Extra for the margins on top and bottom of the ul element

		const data: DialogData<{ header: string, message: string, buttonText: string, bullets: string[] }> = {
			data: { header, message, buttonText, bullets },
		};

		return this.dialogActivityResolver<MessageDialog, { header: string, message: string, buttonText: string }>(MessageDialog, data, reqW, reqH);
	}


	/**
	 *  Create and show a message dialog asking the user to select yes or no.
	 * @param message The displayed text, which can be a literal string or a LabelKey if it needs to be translated.
	 * @param yesButtonText The text for the Yes button, which can be a literal string or a LabelKey if it needs to be translated.
	 * @param noButtonText The text for the No button, which can be a literal string or a LabelKey if it needs to be translated.
	 * @param bullets An array of strings to display as bullet items underneath the message. The requested height will be adjusted with an extra 21px for each bullet to try and make the variable number bullets visible.
	 * @param reqW the requested width of the dialog. It may be different depending on the screen size. Default: 400px
	 * @param reqH the requested height of the dialog. It may be different depending on the screen size. Default: 300px
	 */
	async confirm(
		message: string,
		yesButtonText: string = 'Yes',
		noButtonText: string = 'No',
		bullets: string[] = [],
		reqW = 400,
		reqH = 300,
	): Promise<boolean> {

		if (bullets.length) reqH += (bullets.length) * 21 + 35;	// Extra for the margins on top and bottom of the ul element

		const data: DialogData<{ message: string, yesButtonText: string, noButtonText: string, bullets: string[] }> = {
			data: { message, yesButtonText, noButtonText, bullets },
		};

		const activity = await this.dialogActivityResolver<ConfirmationDialog, { message: string, yesButtonText: string, noButtonText: string, bullets: string[] }>(ConfirmationDialog, data, reqW, reqH);
		return activity?.id == 'yes';
	}

	/**
	 * Create and show a dialog with an Angular form.
	 * VALUES is a generic type that represents the shape of the form values. It should be an object with keys corresponding to the form fields and values corresponding to the field values.
	 * @param dialogData The data for the Angular form dialog.
	 * @param reqW The requested width of the dialog.
	 * @param reqH The requested height of the dialog.
	 * @returns A promise that resolves to the dialog action.
	 */
	async showAngularForm<VALUES>(dialogData: AngularFormDialogData<VALUES>, reqW: number, reqH: number): Promise<DialogAction> {
		return this.dialogActivityResolver<AngularFormDialog<VALUES>, AngularFormDialogData<VALUES>>(AngularFormDialog<VALUES>, { data: dialogData }, reqW, reqH);
	}


	private openDialog<C, D>(component: ComponentType<C>, data: DialogData<D>, reqW: number, reqH: number) {

		const [initialW, initialH] = this.fitToScreen(reqW, reqH, this.layout.dimensions());

		const dialogRef: MatDialogRef<C> = this.matDialog.open(component, {
			panelClass: 'full-width-mat-dialog',
			width: `${initialW}px`,
			height: `${initialH}px`,
			data: {
				...data,
				dimensions: computed(() => {

					const [width, height] = this.fitToScreen(reqW, reqH, this.layout.dimensions());

					dialogRef?.updateSize(`${width}px`, `${height}px`);

					return {
						dialogWidth: width,
						dialogHeight: height,
						bodyWidth: width,
						/** dialogHeight minus the header height and actions bar height */
						bodyHeight: height - 32 - 45,
					};
				})
			},
		});

		return dialogRef;
	}


	private fitToScreen(w: number, h: number, dimensions: DashboardLayoutDimensions): [number, number] {

		const screenWidth = dimensions.windowWidth - 2 * PADDINGS[dimensions.breakpoint];
		const screenHeight = dimensions.windowHeight - 2 * PADDINGS[dimensions.breakpoint];

		if (w > screenWidth) w = screenWidth;
		if (h > screenHeight) h = screenHeight;

		return [w, h];
	}


	private dialogActivityResolver<C, D>(component: ComponentType<C>, data: DialogData<D>, reqW: number, reqH: number): Promise<DialogAction<any>> {

		return new Promise<DialogAction>(async (resolve, reject) => {
			try {

				const dialogRef = this.openDialog<C, D>(component, data, reqW, reqH);


				if (data.instanceIdentifier) {
					this.activity.emit({
						instanceIdentifier: data.instanceIdentifier,
						dialogClass: component.name,
						activity: 'Open',
						value: data,
					});
				}


				const action: DialogAction = await dialogRef.afterClosed().toPromise();


				if (data.instanceIdentifier) {
					this.activity.emit({
						instanceIdentifier: data.instanceIdentifier,
						dialogClass: component.name,
						activity: 'Close',
						action,
						value: data,
					});
				}

				resolve(action);
			}
			catch (e) {
				reject(e);
			}
		});
	}


	/**
	 * Close any open dialogs.
	 */
	closeAll() {
		this.matDialog.closeAll();
	}
}
